import { useQuery } from '@tanstack/react-query'
import { Building2, IdCard, Pencil, Plus, Trash2 } from 'lucide-react'
import { useState, type SyntheticEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { toApiError } from '@/api/errors'
import { EmptyState, ErrorState } from '@/components/feedback/States'
import { toast } from '@/components/feedback/toast-store'
import { FormField } from '@/components/forms/FormField'
import { Button, IconButton } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { Dialog } from '@/components/ui/Dialog'
import { Input } from '@/components/ui/Input'
import { PageHeader } from '@/components/ui/PageHeader'
import { Skeleton } from '@/components/ui/Skeleton'
import { Can } from '@/features/auth/guards'
import { setupItemsQuery, useDeleteSetupItem, useSaveSetupItem, type SetupKind } from '@/features/staff/api'
import type { NamedItem } from '@/features/staff/types'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'

const copy = {
  departments: {
    title: 'Departments',
    description: 'Organisational units staff belong to.',
    create: 'New department',
    rename: 'Rename department',
    remove: 'Delete department?',
    empty: 'No departments yet',
    icon: Building2,
  },
  designations: {
    title: 'Designations',
    description: 'Job titles such as Teacher, Head of Department or Accountant.',
    create: 'New designation',
    rename: 'Rename designation',
    remove: 'Delete designation?',
    empty: 'No designations yet',
    icon: IdCard,
  },
}

function NameDialog({
  kind,
  item,
  open,
  onOpenChange,
}: {
  kind: SetupKind
  item: NamedItem | null
  open: boolean
  onOpenChange: (o: boolean) => void
}) {
  const { t } = useTranslation('staff')
  const save = useSaveSetupItem(kind)
  const [name, setName] = useState(item?.name ?? '')
  const [error, setError] = useState<string | undefined>()

  const submit = (e: SyntheticEvent) => {
    e.preventDefault()
    if (!name.trim()) {
      setError(t('Enter a name.'))
      return
    }
    save.mutate(
      { id: item?.id, name: name.trim() },
      {
        onSuccess: () => {
          toast.success(item ? t('Renamed') : t('Created'), name.trim())
          onOpenChange(false)
        },
        onError: (err) => setError(toApiError(err).violations[0]?.message ?? toApiError(err).message),
      },
    )
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      size="sm"
      title={item ? t(copy[kind].rename) : t(copy[kind].create)}
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            {t('Cancel')}
          </Button>
          <Button type="submit" form="name-form" loading={save.isPending}>
            {t('Save')}
          </Button>
        </>
      }
    >
      <form id="name-form" onSubmit={submit} noValidate>
        <FormField label={t('Name')} error={error} required>
          <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus maxLength={100} />
        </FormField>
      </form>
    </Dialog>
  )
}

export function StaffSetupPage({ kind }: { kind: SetupKind }) {
  const { t } = useTranslation('staff')
  const meta = copy[kind]
  useDocumentTitle(meta.title)
  const list = useQuery(setupItemsQuery(kind))
  const remove = useDeleteSetupItem(kind)
  const [editing, setEditing] = useState<NamedItem | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [deleting, setDeleting] = useState<NamedItem | null>(null)
  const Icon = meta.icon

  return (
    <>
      <PageHeader
        title={t(meta.title)}
        description={t(meta.description)}
        breadcrumbs={[{ label: t('Staff'), to: '/admin/staff' }, { label: t(meta.title) }]}
        homeHref="/admin"
        actions={
          <Can permission="staff.manage">
            <Button
              leftIcon={<Plus />}
              onClick={() => {
                setEditing(null)
                setFormOpen(true)
              }}
            >
              {t(meta.create)}
            </Button>
          </Can>
        }
      />
      {list.error ? (
        <ErrorState onRetry={() => void list.refetch()} />
      ) : (
        <Card>
          {list.isPending ? (
            <div className="space-y-3 p-5">
              {Array.from({ length: 5 }, (_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : list.data.length === 0 ? (
            <EmptyState icon={<Icon />} title={t(meta.empty)} />
          ) : (
            <ul className="divide-y divide-line">
              {list.data.map((item) => (
                <li key={item.id} className="flex items-center gap-3 px-5 py-3.5">
                  <span className="grid size-9 place-items-center rounded-control bg-brand-50 text-brand-600">
                    <Icon className="size-4" aria-hidden="true" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium text-ink">{item.name}</p>
                    <p className="text-xs text-muted">
                      {t('{{count}} staff members', { count: item.staffCount })}
                    </p>
                  </div>
                  <Can permission="staff.manage">
                    <IconButton
                      label={t('Rename {{name}}', { name: item.name })}
                      icon={<Pencil />}
                      onClick={() => {
                        setEditing(item)
                        setFormOpen(true)
                      }}
                    />
                    <IconButton
                      label={t('Delete {{name}}', { name: item.name })}
                      icon={<Trash2 />}
                      disabled={item.staffCount > 0}
                      onClick={() => setDeleting(item)}
                    />
                  </Can>
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}
      {formOpen ? <NameDialog kind={kind} item={editing} open onOpenChange={setFormOpen} /> : null}
      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(o) => (o ? undefined : setDeleting(null))}
        title={t(meta.remove)}
        description={deleting ? t('“{{name}}” will be removed.', { name: deleting.name }) : undefined}
        confirmLabel={t('Delete')}
        loading={remove.isPending}
        onConfirm={() =>
          deleting &&
          remove.mutate(deleting.id, {
            onSuccess: () => {
              toast.success(t('Deleted'))
              setDeleting(null)
            },
            onError: (e) => toast.error(t('Could not delete'), toApiError(e).message),
          })
        }
      />
    </>
  )
}
